import { useState, useEffect } from 'react';
import { ref, onValue, push, set, remove, update } from 'firebase/database';
import { db } from '../firebase';

export const useProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const projectsRef = ref(db, 'projects');
    const unsubscribe = onValue(projectsRef, (snapshot) => {
      const data = snapshot.val();
      const list = data ? Object.entries(data).map(([id, val]) => ({ id, ...val })) : [];
      
      // Sort by position (ascending), then by createdAt as a fallback
      const sortedList = list.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      
      setProjects(sortedList);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // CREATE: Auto-assign position to the end of the list
  const addProject = async (projectData) => {
    const newRef = push(ref(db, 'projects'));
    const currentMaxPosition = projects.length > 0 
      ? Math.max(...projects.map(p => p.position ?? 0)) 
      : -1;

    return await set(newRef, { 
      ...projectData, 
      position: currentMaxPosition + 1, // Add to the end
      createdAt: Date.now() 
    });
  };

  // UPDATE
  const updateProject = async (projectId, projectData) => {
    const projectRef = ref(db, `projects/${projectId}`);
    return await update(projectRef, { ...projectData, updatedAt: Date.now() });
  };

  // REORDER: Top 1% optimized bulk update
  const reorderProjects = async (newOrderList) => {
    const updates = {};
    newOrderList.forEach((project, index) => {
      // We only update the position field for each project ID
      updates[`projects/${project.id}/position`] = index;
    });
    
    // Perform a single atomic update to Firebase
    return await update(ref(db), updates);
  };

  // DELETE
  const deleteProject = async (projectId) => {
    const projectRef = ref(db, `projects/${projectId}`);
    return await remove(projectRef);
  };

  return { projects, loading, addProject, updateProject, deleteProject, reorderProjects };
};